// SkipEnquiryContactLog — embedded panel on the enquiry detail page.
//
// Shows the list of `skip_enquiry_contacts` rows newest-first, plus an
// inline "Log Contact" form that fires POST /skip/enquiries/:enc_id/contacts.
// Wraps the v1 hooks `useSkipEnquiryContacts` + `useAddSkipEnquiryContact`.
//
// Schema notes (from server/HANDOFF.md): the table uses `contact_method`
// (NOT `contact_type`) and `outcome` enums. Values are honoured here.

import { useState } from 'react';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import {
  useAddSkipEnquiryContact,
  useSkipEnquiryContacts,
  type AddContactPayload,
} from '../../hooks/useSkipEnquiries';
import { extractErrorMessage } from '../../../../utils/errorMessage';
import styles from './SkipEnquiryContactLog.module.css';

const CONTACT_METHODS: AddContactPayload['contact_method'][] = [
  'phone',
  'email',
  'sms',
  'in_person',
  'other',
];

const OUTCOMES: NonNullable<AddContactPayload['outcome']>[] = [
  'answered',
  'no_answer',
  'voicemail',
  'emailed',
  'texted',
  'other',
];

const METHOD_LABELS: Record<AddContactPayload['contact_method'], string> = {
  phone: 'Phone',
  email: 'Email',
  sms: 'SMS',
  in_person: 'In person',
  other: 'Other',
};

const OUTCOME_LABELS: Record<NonNullable<AddContactPayload['outcome']>, string> = {
  answered: 'Answered',
  no_answer: 'No answer',
  voicemail: 'Voicemail',
  emailed: 'Emailed',
  texted: 'Texted',
  other: 'Other',
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export interface SkipEnquiryContactLogProps {
  enquiryEncId: string;
  /** When true, hides the "Log Contact" form (e.g. terminal-status enquiries). */
  readOnly?: boolean;
}

export function SkipEnquiryContactLog({
  enquiryEncId,
  readOnly = false,
}: SkipEnquiryContactLogProps): JSX.Element {
  const contactsQuery = useSkipEnquiryContacts(enquiryEncId);
  const addContact = useAddSkipEnquiryContact(enquiryEncId);

  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<AddContactPayload['contact_method']>('phone');
  const [outcome, setOutcome] = useState<NonNullable<AddContactPayload['outcome']>>('answered');
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setMethod('phone');
    setOutcome('answered');
    setNotes('');
    setFollowUp('');
    setError(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!notes.trim()) {
      setError('Notes are required.');
      return;
    }
    const payload: AddContactPayload = {
      contact_method: method,
      outcome,
      notes: notes.trim(),
    };
    if (followUp) payload.follow_up_date = followUp;
    addContact.mutate(payload, {
      onSuccess: () => {
        reset();
        setOpen(false);
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <Card>
      <header className={styles.header}>
        <h2 className={styles.heading}>Contact Log</h2>
        {!readOnly ? (
          <Button
            variant={open ? 'secondary' : 'primary'}
            onClick={() => {
              if (open) reset();
              setOpen((v) => !v);
            }}
          >
            {open ? 'Cancel' : 'Log Contact'}
          </Button>
        ) : null}
      </header>

      {open ? (
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <label className={styles.field}>
              <span>Contact method</span>
              <select
                value={method}
                onChange={(e) =>
                  setMethod(e.target.value as AddContactPayload['contact_method'])
                }
              >
                {CONTACT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {METHOD_LABELS[m]}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Outcome</span>
              <select
                value={outcome}
                onChange={(e) =>
                  setOutcome(
                    e.target.value as NonNullable<AddContactPayload['outcome']>,
                  )
                }
              >
                {OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {OUTCOME_LABELS[o]}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Follow-up date</span>
              <input
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
              />
            </label>
          </div>
          <label className={styles.field}>
            <span>Notes</span>
            <textarea
              required
              rows={3}
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What happened on this contact?"
            />
          </label>
          {error ? <p className={styles.error}>{error}</p> : null}
          <div className={styles.actions}>
            <Button type="submit" variant="primary" disabled={addContact.isPending}>
              {addContact.isPending ? 'Saving…' : 'Save Contact'}
            </Button>
          </div>
        </form>
      ) : null}

      {contactsQuery.isLoading ? (
        <p className={styles.empty}>Loading contacts…</p>
      ) : (contactsQuery.data?.contacts.length ?? 0) === 0 ? (
        <p className={styles.empty}>No contacts logged yet.</p>
      ) : (
        <ul className={styles.list}>
          {contactsQuery.data?.contacts.map((c) => (
            <li key={c.id} className={styles.item}>
              <header className={styles.itemHeader}>
                <span className={styles.method}>{METHOD_LABELS[c.contact_method]}</span>
                <span className={styles.outcome}>{OUTCOME_LABELS[c.outcome]}</span>
                <span className={styles.timestamp}>{fmtDateTime(c.contacted_at)}</span>
              </header>
              {c.notes ? <p className={styles.notes}>{c.notes}</p> : null}
              {c.contacted_by_name ? (
                <p className={styles.byline}>by {c.contacted_by_name}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
